/**
 * AirDashPanel — Nakhon Ratchasima air-quality attention score, bridged from
 * the public AirDash national dust/PM2.5 watch board (air.nonarkara.org).
 *
 * Complements AqiBadge (Sikhio's own live Air4Thai/Open-Meteo station
 * reading) rather than replacing it — this adds the province-wide
 * attention score, a short PM2.5 forecast, and the rain-washout outlook
 * AqiBadge doesn't carry.
 */

import type { AirDashBrief, FallbackTier } from "@sikhio/shared";
import { PanelHeader } from "./PanelHeader";
import { ActionChecklist } from "./ActionChecklist";
import { Arrow } from "./Arrow";

interface Props {
  data: AirDashBrief | null;
  loading: boolean;
  ageMinutes?: number;
  fallbackTier?: FallbackTier;
}

const BAND_COLOR: Record<string, string> = {
  normal: "var(--good)",
  watch: "var(--gold)",
  elevated: "var(--warn)",
  high: "var(--bad)",
};

const WASHOUT_LABEL_TH: Record<string, string> = {
  none: "ไม่ช่วย",
  light: "ช่วยเล็กน้อย",
  moderate: "ช่วยปานกลาง",
  strong: "ช่วยมาก",
};

export function AirDashPanel({ data, loading, ageMinutes, fallbackTier }: Props) {
  if (loading && !data) {
    return (
      <div className="col">
        <div className="eyebrow">ฝุ่น PM2.5 // AIRDASH</div>
        <div className="skeleton" style={{ height: 12, marginTop: 8 }} />
        <div className="skeleton" style={{ height: 12, marginTop: 6, width: "70%" }} />
      </div>
    );
  }

  const risk = data?.risk;
  if (!risk) return null;

  const col = BAND_COLOR[risk.band] ?? "var(--data)";

  return (
    <div className="col" style={{ gap: 4 }}>
      <PanelHeader
        title="ฝุ่น PM2.5 "
        fallbackTier={fallbackTier}
        ageMinutes={ageMinutes}
        source="air.nonarkara.org"
        actions={
          <a href={data?.sourceUrl} target="_blank" rel="noreferrer" className="caption mono">
            เปิด <Arrow direction="up-right" />
          </a>
        }
      />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <span className="mono" style={{ fontSize: "var(--size-h3)", color: col, fontWeight: 700 }}>
          {risk.pm25 != null ? `${risk.pm25} µg/m³` : "—"}
        </span>
        <span className="eyebrow" style={{ color: col }}>
          {risk.headTh}
        </span>
      </div>
      <div className="caption" style={{ marginTop: 4 }}>{risk.actionTh}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px", marginTop: 6 }}>
        {risk.aqi != null && (
          <>
            <span className="caption">AQI</span>
            <span className="mono caption" style={{ textAlign: "right" }}>{risk.aqi}</span>
          </>
        )}
        {risk.pm25Forecast24h != null && (
          <>
            <span className="caption">คาดการณ์ 24 ชม.</span>
            <span className="mono caption" style={{ textAlign: "right" }}>{risk.pm25Forecast24h.toFixed(1)} µg/m³</span>
          </>
        )}
        {risk.washoutBand != null && (
          <>
            <span className="caption">ฝนช่วยล้างฝุ่น</span>
            <span className="mono caption" style={{ textAlign: "right" }}>
              {WASHOUT_LABEL_TH[risk.washoutBand] ?? risk.washoutBand}
            </span>
          </>
        )}
      </div>
      {risk.reasons.length > 0 && (
        <ul className="caption" style={{ margin: "4px 0 0", paddingLeft: 16, color: "var(--text-2)" }}>
          {risk.reasons.map((r) => (
            <li key={r.th}>{r.th}</li>
          ))}
        </ul>
      )}
      <ActionChecklist panelId="air" band={risk.band} items={risk.checklist} />
      <div className="caption" style={{ marginTop: 4, color: "var(--text-3)" }}>
        {risk.disclaimerTh}
      </div>
    </div>
  );
}
